import React, { useCallback, useRef, useState } from 'react';
import { IInvoiceFullObject } from '../../Interfaces/IInvoice';
import { State } from '../../redux/mainReducer';
import { useSelector } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { AllowedFileTypesCard } from '../../Document/AllowedFileTypesCard';
import { IMIMEType } from '../../Interfaces/IMIMEType';
import { IDocument } from '../../Interfaces/IDocument';
import { UploadFile } from '@mui/icons-material';
import { DocumentOverview } from '../../Document/DocumentOverview';
import { IDocumentDownloaded } from '../../Interfaces/IDocumentDownloaded';
import { useCheckDocumentForInvoice } from '../../hooks/reactQuerys/InvoiceQueries';
import { InvoiceTable } from './InvoiceTable';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalRole } from '../../Interfaces/ILegalRole';
import { ILegalTransactionType } from '../../Interfaces/ILegalTransactionType';
import { ILegalTransaction } from '../../Interfaces/ILegalTransaction';
import { FSDirectorySearch } from '../../FSDirectory/FSDirectorySearch';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { ILegalTransactionRecordNumber } from '../../Interfaces/ILegalTransactionRecordNumber';
import { checkDocumentArrayUtil } from './checkDocumentArrayUtil';
import { ILegalTransactionTransactionNumber } from '../../Interfaces/ILegalTransactionTransactionNumber';

interface IProps {
  legalTransaction: ILegalTransaction;
  documentArray: IDocument[];
  setDocumentArray: (newDocuments: IDocument[]) => void;
  downloadedArray?: IDocumentDownloaded[];
  invoiceObjectArray: IInvoiceFullObject[];
  setInvoiceObjectArray: (invoices: IInvoiceFullObject[]) => void;
  assignedLegalClients: ILegalClient[];
  setLegalClients: (clients: ILegalClient[]) => void;
  legalRoles: ILegalRole[];
  legalTransactionTypes: ILegalTransactionType[];
  hasDirInvoice: boolean;
  recordNumberArray: ILegalTransactionRecordNumber[];
  transactionNumberArray: ILegalTransactionTransactionNumber[];
  isDisableAddDocs?: boolean;
}

export const InvoiceTab: React.FC<IProps> = ({
  legalTransaction,
  documentArray,
  setDocumentArray,
  downloadedArray,
  invoiceObjectArray,
  setInvoiceObjectArray,
  assignedLegalClients,
  legalRoles,
  setLegalClients,
  legalTransactionTypes,
  hasDirInvoice,
  recordNumberArray,
  transactionNumberArray,
  isDisableAddDocs = false,
}) => {
  const { checkDocumentForInvoice, isPendingCheckDocumentForInvoice } =
    useCheckDocumentForInvoice();
  const mimeTypeArray = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );
  const [enableUpload, setEnableUpload] = useState(false);
  const [scanningFileName, setScanningFileName] = useState<string | null>(null);
  const inputUpload = useRef<HTMLInputElement | null>(null);

  async function fileToBase64(file: File) {
    const result_base64 = await new Promise(resolve => {
      const fileReader = new FileReader();
      fileReader.onload = () => resolve(fileReader.result);
      fileReader.readAsDataURL(file);
    });
    return String(result_base64).split(',')[1];
  }
  const checkDocumentArray = (uploadArray: IDocument[]) => {
    return checkDocumentArrayUtil({
      uploadArray,
      legalClients: assignedLegalClients,
      legalRoles,
      legalTransaction,
      documentArray,
      invoiceObjectArray,
      setDocumentArray,
      setInvoiceObjectArray,
      setLegalClients,
      onScanProgress: setScanningFileName,
      checkDocumentForInvoice,
    });
  };
  const handleFilesUpload = async (acceptedFiles: File[]) => {
    const uploadArray: IDocument[] = [];
    let minIdOffset = -1;

    if (documentArray.length > 0) {
      const minIdArray = Math.min(
        ...documentArray.map(document => document.idDocument)
      );

      if (minIdArray <= minIdOffset) {
        minIdOffset = minIdArray - 1;
      }
    }

    let index = 0;

    for (const currentFile of acceptedFiles) {
      const fileName: string = String(currentFile['name']);
      const mimeType: IMIMEType | undefined = mimeTypeArray.find(
        mimeType => mimeType.MIMEType === currentFile['type']
      );

      if (mimeType) {
        uploadArray.push({
          idDocument: minIdOffset - index,
          FileName: fileName,
          idMIMEType: mimeType.idMIMEType,
          Data: await fileToBase64(currentFile),
          Created_at: new Date().toISOString(),
        } as IDocument);
      }
      index = index + 1;
    }
    checkDocumentArray(uploadArray);
  };
  const handleNormalUpload = (files: null | FileList) => {
    if (files) {
      const acceptedFiles: File[] = Array.from(files);
      handleFilesUpload(acceptedFiles);
    }
  };
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      handleFilesUpload(acceptedFiles);
      setEnableUpload(false);
    },
    [documentArray]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
  });

  const dropZoneTSX = () => {
    return (
      <div>
        <input
          {...getInputProps()}
          accept={String(
            mimeTypeArray.map(mimeType => mimeType.MIMEType).join(', ')
          )}
        />
        <AllowedFileTypesCard />
      </div>
    );
  };

  return (
    <>
      {(isPendingCheckDocumentForInvoice || scanningFileName) && (
        <Dialog open>
          <DialogContent>
            <>
              <Typography variant={'h5'} sx={{ mb: 2, mt: 0 }}>
                Rechnungen
              </Typography>
              {scanningFileName ? (
                <>
                  Lese QR-Code &amp; Urkundennummer aus „{scanningFileName}"
                  &nbsp;
                  <CircularProgress size={18} />
                </>
              ) : (
                <>
                  Untersuche Dokument(e) ... <CircularProgress size={18} />
                </>
              )}
            </>
          </DialogContent>
        </Dialog>
      )}

      <div {...getRootProps()}>
        <input
          hidden
          ref={inputUpload}
          type='file'
          multiple
          accept={String(
            mimeTypeArray.map(mimeType => mimeType.MIMEType).join(', ')
          )}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleNormalUpload(event.target.files)
          }
        />

        {!isDisableAddDocs && (
          <Typography variant={'h5'} sx={{ mb: 2, mt: 0 }}>
            Rechnungen
            <IconButton
              title='Daten über Auswähl hochladen'
              onClick={() => inputUpload && inputUpload.current?.click()}
              sx={{ float: 'right' }}
            >
              <UploadFile />
            </IconButton>
            <br />
            <Typography variant='caption'>
              (Hier können Sie Ihre Dokumente per <i>Drag & Drop</i> hochladen)
            </Typography>
          </Typography>
        )}

        {(isDragActive || enableUpload) && !isDisableAddDocs && dropZoneTSX()}

        <Typography variant={'h6'} sx={{ mt: 2 }}>
          Erkannte Rechnungen
        </Typography>
        <InvoiceTable
          idLegalTransaction={legalTransaction.idLegalTransaction}
          downloadedArray={downloadedArray}
          invoiceObjectArray={invoiceObjectArray}
          setInvoiceObjectArray={setInvoiceObjectArray}
          assignedLegalClients={assignedLegalClients}
          setLegalClients={setLegalClients}
          legalRoles={legalRoles}
          legalTransactionTypes={legalTransactionTypes}
          recordNumberArray={recordNumberArray}
        />

        <DocumentOverview
          key='invoice'
          allowRemove={true}
          allowUpload={false}
          titleVariant='h6'
          marginTop={5}
          title='Nicht erkannte Rechnungen'
          documentArray={documentArray}
          setDocumentArray={setDocumentArray}
          downloadedArray={downloadedArray}
          legalClients={assignedLegalClients}
          enableSidepanel={true}
        />
      </div>
      {Boolean(hasDirInvoice) === true && (
        <Accordion sx={{ mt: 5 }}>
          <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
            Ordnersuche
          </AccordionSummary>
          <AccordionDetails>
            <FSDirectorySearch
              key='invoice-fs-search'
              searchArray={recordNumberArray
                .filter(recordNumber => recordNumber.RecordNumberEntry !== '')
                .map(recordNumber => recordNumber.RecordNumberEntry)}
              searchArrayFallback={transactionNumberArray
                .filter(
                  transactionNumber =>
                    transactionNumber.TransactionNumberEntry !== ''
                )
                .map(
                  transactionNumber => transactionNumber.TransactionNumberEntry
                )}
              searchItem='invoice'
              documentArray={[]}
              setDocumentArray={checkDocumentArray}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};
